import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, ShoppingCart, DollarSign, GraduationCap, BookOpen, ArrowRight } from 'lucide-react';
import { api, formatMoney, formatDateTime } from '../../lib/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api('/api/admin/stats'),
      api('/api/admin/purchases'),
      api('/api/admin/users'),
    ])
      .then(([s, p, u]) => {
        setStats(s);
        setRecentPurchases((p.purchases || []).slice(0, 8));
        setRecentUsers((u.users || []).slice(0, 8));
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-8 text-slate-500">Loading…</div>;
  }

  const cards = [
    { label: 'Total users',          value: stats.user_count.toLocaleString(),               icon: Users,         color: 'bg-blue-50 text-blue-600' },
    { label: 'Total purchases',      value: stats.purchase_count.toLocaleString(),           icon: ShoppingCart,  color: 'bg-emerald-50 text-emerald-600' },
    { label: 'Revenue (last 30d)',   value: formatMoney(stats.revenue_30d_cents),            icon: DollarSign,    color: 'bg-amber-50 text-amber-600' },
    { label: 'Revenue (all time)',   value: formatMoney(stats.revenue_total_cents),          icon: DollarSign,    color: 'bg-violet-50 text-violet-600' },
    { label: 'Enrollments',          value: stats.enrollment_count.toLocaleString(),         icon: BookOpen,      color: 'bg-sky-50 text-sky-600' },
    { label: 'Completions',          value: stats.completion_count.toLocaleString(),         icon: GraduationCap, color: 'bg-rose-50 text-rose-600' },
  ];

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.color}`}>
                <c.icon size={20} />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{c.label}</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Recent purchases" linkTo="/admin/purchases">
          {recentPurchases.length === 0 ? (
            <Empty>No purchases yet.</Empty>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recentPurchases.map((p) => (
                <li key={p.id} className="py-3 flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.course_title}</div>
                    <div className="text-xs text-slate-500 truncate">{p.user_email}</div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="font-semibold">{formatMoney(p.amount_cents, p.currency)}</div>
                    <div className="text-xs text-slate-500">{formatDateTime(p.completed_at || p.created_at)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Recent signups" linkTo="/admin/users">
          {recentUsers.length === 0 ? (
            <Empty>No users yet.</Empty>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recentUsers.map((u) => (
                <li key={u.id} className="py-3 flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{u.full_name}</div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  </div>
                  <div className="text-xs text-slate-500 shrink-0 ml-3">
                    {formatDateTime(u.created_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, linkTo, children }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <h2 className="font-semibold">{title}</h2>
        {linkTo && (
          <Link to={linkTo} className="text-xs text-brand font-medium flex items-center gap-1 hover:underline">
            View all <ArrowRight size={12} />
          </Link>
        )}
      </div>
      <div className="px-5 py-2">{children}</div>
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-slate-500 py-6 text-center">{children}</p>;
}
